import { Box } from '@material-ui/core';
import { withStyles, withTheme } from '@material-ui/core/styles';
import { useAuth } from '@saltus/auth';
import {
  captureException,
  ErrorBoundary,
  setUser as setUserForSentry,
} from '@sentry/react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RouterOutlet from '../../pages/_router';
import debug from '../../services/debug';
import mobileLogin from '../../services/mobileLogin';
import { retry } from '../../utils/promise';
import AppSecure from '../app-secure/container';
import AppTheme from '../app-theme/container';
import Loading from '../loading/component';
import useAnalytics from '../../hooks/useAnalytics';
import useSessionRecovery from '../../hooks/useSessionRecovery';
import MobileLoginProblem from '../mobile-login-error/component';
import PortalError from '../portal-error/component';
import styles from './styles';
import ProfileProvider from '../../providers/ProfileProvider';

const AUTH_STATE_SIGNED_IN = 'authenticated';

function App({ classes }) {
  const { authStatus, loading, user } = useAuth();
  const { recordEvent } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileLoginProblem, setMobileLoginProblem] = useState();
  const showMobileLoginProblem = useCallback(
    () => setMobileLoginProblem(true),
    [],
  );

  useSessionRecovery({
    authStatus,
    loading,
    onGiveUp: showMobileLoginProblem,
    user,
  });
  const themeOveride = (state) =>
    state === AUTH_STATE_SIGNED_IN && user ? '' : 'dark';

  useEffect(() => {
    debug('Location', JSON.stringify(location));

    const excludedPaths = ['/', '/profile'];

    if (!excludedPaths.find((pathname) => pathname === location.pathname)) {
      recordEvent({
        category: 'SaltusPortal',
        action: 'Display',
        label: location.pathname,
      });
    }
  }, [recordEvent, location]);

  useEffect(() => {
    if (user && user.attributes?.email) {
      // TODO: Add app version to logging
      setUserForSentry({
        email: user.attributes.email,
      });
    } else {
      setUserForSentry(null);
    }
  }, [user]);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      debug('nextAuthState', authStatus);

      if (window.ReactNativeWebView !== undefined && authStatus) {
        window.ReactNativeWebView.postMessage(authStatus);
      }
    }
  }, [authStatus, user]);

  const handleMessageEvent = useCallback(
    async (event) => {
      if (event.data && event.data !== 'undefined') {
        let msg = null;

        try {
          msg =
            typeof event.data === 'string'
              ? JSON.parse(event.data)
              : event.data;
        } catch (e) {
          /* empty */
        }

        if (
          !msg ||
          (msg.source && msg.source.startsWith('react-devtools')) ||
          (msg.type && msg.type.startsWith('webpack'))
        ) {
          return;
        }

        debug('handleMessageEvent', msg);

        if (msg.messageType === 'changeRoute') {
          navigate(`/${msg.route}`);
        } else if (msg.messageType === 'mobileLogin') {
          try {
            debug('try mobile login', event);

            await retry(
              () => mobileLogin(msg.token, msg.reload),
              'mobileLogin',
            );
          } catch (error) {
            captureException(error);

            setMobileLoginProblem(true);
          }
        }
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (window.ReactNativeWebView) {
      if (authStatus === 'unauthenticated') {
        // Let React Native know we're ready to accept messages
        window.ReactNativeWebView.postMessage('ready');
      }

      document.addEventListener('message', handleMessageEvent, true);
      window.addEventListener('message', handleMessageEvent, true);
    }

    return () => {
      if (window.ReactNativeWebView) {
        document.removeEventListener('message', handleMessageEvent, true);
        window.removeEventListener('message', handleMessageEvent, true);
      }
    };
  }, [authStatus, handleMessageEvent]);

  useEffect(() => {
    debug('App authState', authStatus);
  }, [authStatus]);

  useEffect(() => {
    debug('App user', user);
  }, [user]);

  if (mobileLoginProblem) {
    return (
      <Box className={classes.root} data-testid="app-root">
        <AppTheme themeOveride={themeOveride(authStatus)}>
          <ErrorBoundary fallback={<PortalError />} showDialog={false}>
            <MobileLoginProblem />
          </ErrorBoundary>
        </AppTheme>
      </Box>
    );
  }

  return (
    <Box className={classes.root} data-testid="app-root">
      <AppTheme themeOveride={themeOveride(authStatus)}>
        <ErrorBoundary fallback={<PortalError />} showDialog={false}>
          {(() => {
            if (authStatus === 'authenticated' && user) {
              return (
                <ProfileProvider>
                  <AppSecure />
                </ProfileProvider>
              );
            }

            if (window.ReactNativeWebView) {
              debug('App ReactNativeWebView awaiting AppSecure to mount', user);

              return <Loading />;
            }

            return <RouterOutlet />;
          })()}
        </ErrorBoundary>
      </AppTheme>
    </Box>
  );
}

App.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
};

App.defaultProps = {
  classes: {},
};

export default withTheme(withStyles(styles)(App));
